import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { X, Search, Plus } from 'lucide-react';
import type { POSSupplier } from '@/types';
import {
  validateSupplierForm,
  normalizeSupplierMobile,
  getApiErrorMessage,
} from '@/utils/supplierValidation';

interface SupplierSelectProps {
  value: string;
  onChange: (supplierId: string) => void;
  required?: boolean;
  mode?: 'all' | 'item_stock_in';
  item?: string;
  variant?: string | null;
  requiresVariant?: boolean;
  onSupplierPick?: (supplier: POSSupplier) => void;
  allowQuickAdd?: boolean;
  disabled?: boolean;
}

export default function SupplierSelect({
  value,
  onChange,
  required,
  mode = 'all',
  item,
  variant,
  requiresVariant = false,
  onSupplierPick,
  allowQuickAdd = true,
  disabled = false,
}: SupplierSelectProps) {
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [addError, setAddError] = useState('');
  const qc = useQueryClient();

  const isItemMode = mode === 'item_stock_in';
  const needsVariant = isItemMode && requiresVariant && !variant;

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: isItemMode
      ? ['item-suppliers', item, variant || '', search]
      : ['suppliers', search],
    queryFn: async () => {
      if (isItemMode) {
        if (!item || needsVariant) return [];
        const res = await posApi.getItemSuppliers({
          item,
          variant: requiresVariant ? variant || undefined : undefined,
          search,
          limit: 100,
        });
        return res.data.message.suppliers as POSSupplier[];
      }
      const res = await posApi.getSuppliers({ search, limit: 100 });
      return res.data.message.suppliers as POSSupplier[];
    },
    enabled: isItemMode ? !!item && !needsVariant : true,
  });

  const addMutation = useMutation({
    mutationFn: (payload: { supplier_name: string; mobile: string }) =>
      posApi.createSupplier(payload),
    onSuccess: (res) => {
      const created = res.data.message as POSSupplier;
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      onChange(created.name);
      onSupplierPick?.(created);
      setShowAdd(false);
      setNewName('');
      setNewMobile('');
      setAddError('');
    },
    onError: (err: unknown) => setAddError(getApiErrorMessage(err, 'Failed to create supplier')),
  });

  const selected = suppliers.find((s) => s.name === value);

  const handleQuickAdd = () => {
    const errors = validateSupplierForm({
      supplier_name: newName,
      mobile: newMobile,
      email: '',
    });
    if (errors.supplier_name || errors.mobile) {
      setAddError(errors.supplier_name || errors.mobile || 'Invalid supplier details');
      return;
    }
    addMutation.mutate({
      supplier_name: newName.trim(),
      mobile: newMobile.trim() ? normalizeSupplierMobile(newMobile) : '',
    });
  };

  const handleChange = (supplierId: string) => {
    onChange(supplierId);
    const picked = suppliers.find((s) => s.name === supplierId);
    if (picked) onSupplierPick?.(picked);
  };

  const emptyMessage = isItemMode
    ? 'No supplier has supplied this item.'
    : 'Select supplier...';

  return (
    <div className="space-y-2">
      {!disabled && (
        <div className="flex gap-2">
          <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            className="input-field pl-9"
            placeholder="Search suppliers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={needsVariant || disabled}
          />
        </div>
        {allowQuickAdd && !disabled && (
          <button
            type="button"
            onClick={() => { setShowAdd(true); setAddError(''); }}
            className="btn-secondary px-3 shrink-0"
            title="Add supplier"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
      )}

      <select
        required={required}
        className="input-field disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled || needsVariant || (isItemMode && !item)}
      >
        <option value="">
          {needsVariant
            ? 'Select a variant first...'
            : isLoading
              ? 'Loading suppliers...'
              : emptyMessage}
        </option>
        {suppliers.map((s) => (
          <option key={s.name} value={s.name}>
            {s.supplier_name}{s.mobile ? ` · ${s.mobile}` : ''}
          </option>
        ))}
      </select>

      {selected && !disabled && (
        <p className="text-xs text-gray-500">Selected: {selected.supplier_name}</p>
      )}

      {!isLoading && isItemMode && item && !needsVariant && suppliers.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
          No supplier has supplied this item. Stock in from a supplier first.
        </p>
      )}

      {allowQuickAdd && showAdd && (
        <div className="border border-gray-100 rounded-lg p-3 bg-gray-50 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">New supplier</span>
            <button type="button" onClick={() => setShowAdd(false)} className="p-1 hover:bg-gray-200 rounded">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <input
            className="input-field text-sm"
            placeholder="Supplier name * (e.g. Ali & Sons #1)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <input
            className="input-field text-sm"
            placeholder="Mobile (optional, 03001234567)"
            maxLength={11}
            value={newMobile}
            onChange={(e) => setNewMobile(e.target.value.replace(/\D/g, ''))}
          />
          {addError && <p className="text-xs text-red-600">{addError}</p>}
          <button
            type="button"
            disabled={!newName.trim() || addMutation.isPending}
            onClick={handleQuickAdd}
            className="btn-primary text-xs py-1.5 px-3"
          >
            {addMutation.isPending ? 'Saving...' : 'Save supplier'}
          </button>
        </div>
      )}
    </div>
  );
}
